/**
 * Ações do dia a dia (plano, seção 11). As telas só chamam estas funções;
 * nenhuma regra de negócio fica dentro de componente (seção 15.3).
 */
import { amountDue, canTransition, conflictsWith, durationMinOf, endFromStart } from '../domain/appointments';
import { servicesOf } from '../domain/appointment-services';
import { activePackageForAny, packageBalance } from '../domain/packages';
import { type SaleItemInput, saleTotal, stockByProduct, suppliesOf } from '../domain/stock';
import { normalizeWhatsapp } from '../domain/phone';
import { accessState } from '../domain/subscription';
import { type LocalDate, addDays, toMs, todayIn, toIso } from '../domain/time';
import type { LocalDb } from '../db/local-db';
import type {
  Appointment,
  AppointmentStatus,
  AvailabilityWindow,
  Client,
  Location,
  MessageKind,
  Package,
  Payment,
  PaymentMethod,
  Product,
  Professional,
  Sale,
  Service,
  ServiceSupply,
  StockMovement,
  StockUnit,
} from '../schemas/entities';
import { DomainError } from './errors';

export interface NewClientInput {
  name: string;
  whatsapp?: string | null;
  address?: string | null;
  notes?: string | null;
}

export interface CreateAppointmentInput {
  clientId?: string;
  newClient?: NewClientInput;
  /** o primeiro serviço; use serviceIds para marcar vários de uma vez */
  serviceId: string;
  /** vários serviços no mesmo atendimento, na ordem escolhida */
  serviceIds?: string[];
  /** início em UTC (ISO) */
  start: string;
  location?: Location;
  address?: string | null;
  notes?: string | null;
  /** sobrescreve o preço do serviço */
  priceCents?: number;
  /** sobrescreve a duração do serviço */
  durationMin?: number;
  status?: Extract<AppointmentStatus, 'scheduled' | 'confirmed'>;
}

export interface CompleteInput {
  /** null = concluir sem receber agora (fica em "a receber") */
  method: PaymentMethod | null;
  discountCents?: number;
  /** valor recebido; padrão = valor devido */
  amountCents?: number;
  /** usar pacote ativo, se houver (padrão: sim) */
  usePackage?: boolean;
  /** produtos do estoque vendidos junto com o atendimento (mesma forma de pagamento) */
  products?: SaleItemInput[];
}

export interface CompleteResult {
  appointment: Appointment;
  payment: Payment | null;
  packageUsed: { pkg: Package; remaining: number } | null;
  sale: Sale | null;
}

export interface ProductInput {
  name: string;
  unit?: StockUnit;
  pack_size?: number;
  /** preço que pagou por embalagem */
  cost_cents?: number;
  /** preço de venda por embalagem; null = só usa */
  price_cents?: number | null;
  min_qty?: number | null;
}

export interface ActionsContext {
  db: LocalDb;
}

export class LindezaActions {
  constructor(private readonly ctx: ActionsContext) {}

  private get db(): LocalDb {
    return this.ctx.db;
  }

  async professional(): Promise<Professional> {
    const id = this.db.professionalId();
    const p = await this.db.get('professional', id);
    if (!p) throw new DomainError('not_found', 'Perfil não encontrado no aparelho.');
    return p;
  }

  async canCreate(): Promise<boolean> {
    const prof = await this.professional();
    return accessState(prof, this.db.now()).canCreate;
  }

  private async requireCanCreate(): Promise<void> {
    if (!(await this.canCreate())) {
      throw new DomainError('subscription_required', 'Seu teste acabou. Assine para marcar novos atendimentos.');
    }
  }

  // ================================================================ perfil

  async updateSettings(patch: Partial<Professional>): Promise<Professional> {
    return this.db.update('professional', this.db.professionalId(), patch);
  }

  async finishOnboarding(): Promise<Professional> {
    return this.updateSettings({ onboarded_at: this.db.nowIso() });
  }

  // ================================================================ clientes

  async createClient(input: NewClientInput): Promise<Client> {
    const name = input.name.trim();
    if (!name) throw new DomainError('invalid_input', 'Informe o nome do cliente.');
    const whatsapp = input.whatsapp ? normalizeWhatsapp(input.whatsapp) : null;
    if (input.whatsapp && !whatsapp) throw new DomainError('invalid_input', 'WhatsApp inválido. Use DDD + número.');
    if (whatsapp) {
      const existing = await this.findClientByWhatsapp(whatsapp);
      if (existing) return existing;
    }
    return this.db.create('client', {
      name,
      whatsapp,
      address: input.address?.trim() || null,
      notes: input.notes?.trim() || null,
    });
  }

  async updateClient(id: string, patch: Partial<NewClientInput>): Promise<Client> {
    const data: Partial<Client> = {};
    if (patch.name !== undefined) {
      if (!patch.name.trim()) throw new DomainError('invalid_input', 'Informe o nome do cliente.');
      data.name = patch.name.trim();
    }
    if (patch.whatsapp !== undefined) {
      const w = patch.whatsapp ? normalizeWhatsapp(patch.whatsapp) : null;
      if (patch.whatsapp && !w) throw new DomainError('invalid_input', 'WhatsApp inválido. Use DDD + número.');
      data.whatsapp = w;
    }
    if (patch.address !== undefined) data.address = patch.address?.trim() || null;
    if (patch.notes !== undefined) data.notes = patch.notes?.trim() || null;
    return this.db.update('client', id, data);
  }

  async findClientByWhatsapp(whatsapp: string): Promise<Client | undefined> {
    const normalized = normalizeWhatsapp(whatsapp);
    if (!normalized) return undefined;
    const clients = await this.db.all('client');
    return clients.find((c) => c.whatsapp === normalized);
  }

  /** Exclui o cliente e todo o histórico (LGPD, seção 20). */
  async deleteClientWithHistory(id: string): Promise<void> {
    await this.db.batch(async () => {
      const [appointments, payments, packages, sales, movements] = await Promise.all([
        this.db.all('appointment'),
        this.db.all('payment'),
        this.db.all('package'),
        this.db.all('sale'),
        this.db.all('stock_movement'),
      ]);
      const apptIds = new Set(appointments.filter((a) => a.client_id === id).map((a) => a.id));
      // Venda e gasto de produto ficam (o produto saiu do estoque e o dinheiro entrou), só sem o nome dela.
      for (const m of movements) if (m.appointment_id && apptIds.has(m.appointment_id)) await this.db.update('stock_movement', m.id, { appointment_id: null });
      for (const s of sales) if (s.client_id === id || (s.appointment_id && apptIds.has(s.appointment_id))) await this.db.update('sale', s.id, { client_id: null, appointment_id: null });
      for (const p of payments) {
        if (p.sale_id) {
          if (p.client_id === id) await this.db.update('payment', p.id, { client_id: null });
        } else if (p.client_id === id || (p.appointment_id && apptIds.has(p.appointment_id))) await this.db.remove('payment', p.id);
      }
      for (const a of apptIds) await this.db.remove('appointment', a);
      for (const p of packages) if (p.client_id === id) await this.db.remove('package', p.id);
      await this.db.remove('client', id);
    });
  }

  // ================================================================ serviços

  async createService(input: {
    name: string;
    duration_min: number;
    price_cents: number;
    return_days?: number | null;
    visible_on_link?: boolean;
    color?: string | null;
  }): Promise<Service> {
    const services = await this.db.all('service');
    const sort = services.reduce((m, s) => Math.max(m, s.sort_order), -1) + 1;
    return this.db.create('service', { ...input, name: input.name.trim(), sort_order: sort, active: true });
  }

  async updateService(id: string, patch: Partial<Service>): Promise<Service> {
    return this.db.update('service', id, patch);
  }

  /** Serviço some do catálogo, mas o histórico continua mostrando o nome. */
  async archiveService(id: string): Promise<Service> {
    return this.db.update('service', id, { active: false, visible_on_link: false });
  }

  async reorderServices(ids: string[]): Promise<void> {
    await this.db.batch(async () => {
      for (let i = 0; i < ids.length; i++) await this.db.update('service', ids[i]!, { sort_order: i });
    });
  }

  // ================================================================ agenda

  async createAppointment(
    input: CreateAppointmentInput,
  ): Promise<{ appointment: Appointment; client: Client; conflicts: Appointment[] }> {
    await this.requireCanCreate();

    /*
     * Um atendimento pode ter vários serviços ("pé e mão" mais "esmaltação").
     * `serviceId` sozinho continua valendo: é o caminho de quase sempre, e
     * mudar a assinatura obrigaria a mexer em toda chamada existente.
     */
    const ids = input.serviceIds?.length ? input.serviceIds : [input.serviceId];
    if (new Set(ids).size !== ids.length) {
      throw new DomainError('invalid_input', 'Você escolheu o mesmo serviço duas vezes.');
    }

    const escolhidos: Service[] = [];
    for (const id of ids) {
      const s = await this.db.get('service', id);
      if (!s) throw new DomainError('not_found', 'Serviço não encontrado.');
      escolhidos.push(s);
    }
    const service = escolhidos[0]!;
    return this.db.batch(async () => {
      let client: Client | undefined;
      if (input.clientId) client = await this.db.get('client', input.clientId);
      else if (input.newClient) client = await this.createClient(input.newClient);
      if (!client) throw new DomainError('invalid_input', 'Escolha o cliente.');
      // A soma das durações: o horário precisa caber o conjunto.
      const duration = input.durationMin ?? escolhidos.reduce((soma, s) => soma + s.duration_min, 0);
      const start = toIso(input.start);
      const end = endFromStart(start, duration);
      const location = input.location ?? 'studio';
      const all = await this.appointmentsAround(start, end);
      const conflicts = conflictsWith(all, start, end);
      const appointment = await this.db.create('appointment', {
        client_id: client.id,
        service_id: service.id,
        start_utc: start,
        end_utc: end,
        location,
        address: location === 'home' ? (input.address ?? client.address ?? null) : null,
        status: input.status ?? 'scheduled',
        source: 'professional',
        // Soma dos preços: sem isso o caixa discordaria das linhas.
        price_cents: input.priceCents ?? escolhidos.reduce((soma, s) => soma + s.price_cents, 0),
        discount_cents: 0,
        notes: input.notes ?? null,
      });
      /*
       * As linhas nascem junto, no mesmo lote: é delas que o caixa, o estoque
       * e o pacote vão somar. O servidor cria uma se faltarem, mas isso é
       * rede de segurança para app desatualizado — quem está em dia manda as
       * linhas e continua funcionando sem internet.
       *
       * Preço e duração ficam congelados aqui: o serviço muda de preço com o
       * tempo, e o atendimento de março tem de continuar valendo o de março.
       *
       * Com um serviço só e preço ou duração sobrescritos à mão, a linha leva
       * o valor dela — senão o atendimento e a linha discordariam.
       */
      const unico = escolhidos.length === 1;
      for (const [posicao, s] of escolhidos.entries()) {
        await this.db.create('appointment_service', {
          appointment_id: appointment.id,
          service_id: s.id,
          price_cents: unico ? appointment.price_cents : s.price_cents,
          duration_min: unico ? duration : s.duration_min,
          position: posicao,
        });
      }
      return { appointment, client, conflicts };
    });
  }

  async rescheduleAppointment(
    id: string,
    start: string,
    opts: { durationMin?: number } = {},
  ): Promise<{ appointment: Appointment; conflicts: Appointment[] }> {
    const a = await this.requireAppointment(id);
    if (a.status === 'completed') throw new DomainError('completed_locked', 'Atendimento concluído não pode ser remarcado.');
    const duration = opts.durationMin ?? durationMinOf(a);
    const startIso = toIso(start);
    const end = endFromStart(startIso, duration);
    const conflicts = conflictsWith(await this.appointmentsAround(startIso, end), startIso, end, id);
    const status: AppointmentStatus = a.status === 'canceled' || a.status === 'no_show' ? 'scheduled' : a.status;
    const appointment = await this.db.update('appointment', id, {
      start_utc: startIso,
      end_utc: end,
      status,
      overlap_ok: false,
      canceled_at: null,
      canceled_by: null,
    });
    return { appointment, conflicts };
  }

  async setStatus(id: string, to: AppointmentStatus): Promise<Appointment> {
    const a = await this.requireAppointment(id);
    if (!canTransition(a.status, to)) {
      throw new DomainError('invalid_transition', 'Não dá para mudar esse atendimento assim.');
    }
    const patch: Partial<Appointment> = { status: to };
    if (to === 'canceled') {
      patch.canceled_at = this.db.nowIso();
      patch.canceled_by = 'professional';
    } else if (a.status === 'canceled') {
      patch.canceled_at = null;
      patch.canceled_by = null;
    }
    return this.db.update('appointment', id, patch);
  }

  confirm(id: string): Promise<Appointment> {
    return this.setStatus(id, 'confirmed');
  }
  /** Aceitar pedido do link (modo com aprovação). */
  accept(id: string): Promise<Appointment> {
    return this.setStatus(id, 'scheduled');
  }
  cancel(id: string): Promise<Appointment> {
    return this.setStatus(id, 'canceled');
  }
  markNoShow(id: string): Promise<Appointment> {
    return this.setStatus(id, 'no_show');
  }
  /** Desfazer cancelamento ou falta. */
  reopen(id: string): Promise<Appointment> {
    return this.setStatus(id, 'scheduled');
  }

  /** Concluir e cobrar (função 5): valor preenchido, forma de pagamento, desconto, pacote. */
  async completeAppointment(id: string, input: CompleteInput): Promise<CompleteResult> {
    const a = await this.requireAppointment(id);
    if (a.status === 'completed') throw new DomainError('completed_locked', 'Esse atendimento já foi concluído.');
    if (!canTransition(a.status, 'completed')) {
      throw new DomainError('invalid_transition', 'Aceite o pedido antes de concluir.');
    }
    return this.db.batch(async () => {
      const [packages, appointments, linhas] = await Promise.all([
        this.db.all('package'),
        this.db.all('appointment'),
        this.db.all('appointment_service'),
      ]);
      const prof = await this.professional();
      const today = todayIn(prof.timezone, this.db.now());

      /*
       * Os serviços deste atendimento. Cai em service_id quando não há linha:
       * dado de app antigo que ainda não sincronizou.
       */
      const doAtendimento = servicesOf(id, linhas);
      const idsDosServicos = doAtendimento.length > 0 ? doAtendimento.map((l) => l.service_id) : [a.service_id];

      const pkg = input.usePackage === false ? null : activePackageForAny(packages, appointments, a.client_id, idsDosServicos, today);

      /*
       * O pacote cobre UM serviço, não o atendimento. Vira desconto do preço
       * daquele serviço, para quem marcou "pé e mão" com pacote mais
       * "esmaltação" continuar pagando a esmaltação.
       *
       * Sem linha (dado de app antigo), cobre o atendimento inteiro, que é o
       * que ele valia antes.
       */
      const cobertoPeloPacote = pkg
        ? (doAtendimento.find((l) => l.service_id === pkg.pkg.service_id)?.price_cents ?? a.price_cents)
        : 0;
      const discount = Math.max(0, input.discountCents ?? 0);
      const now = this.db.nowIso();

      const appointment = await this.db.update('appointment', id, {
        status: 'completed',
        completed_at: now,
        discount_cents: pkg ? cobertoPeloPacote : discount,
        package_id: pkg ? pkg.pkg.id : null,
      });

      let payment: Payment | null = null;
      const due = amountDue(appointment);
      if (!pkg && input.method && due > 0) {
        payment = await this.db.create('payment', {
          appointment_id: id,
          client_id: a.client_id,
          amount_cents: Math.max(0, input.amountCents ?? due),
          method: input.method,
          paid_at: now,
        });
      }

      const client = await this.db.get('client', a.client_id);
      if (client && (!client.last_visit_at || toMs(client.last_visit_at) < toMs(a.start_utc))) {
        await this.db.update('client', client.id, { last_visit_at: a.start_utc });
      }

      /*
       * O que cada serviço gasta sai do estoque (gasto por serviço, em
       * Ajustes > Serviços). Com vários serviços, cada um gasta o dele: dois
       * serviços que usam a mesma lixa gastam duas.
       */
      const supplies = await this.db.all('service_supply');
      for (const servicoId of idsDosServicos) {
        for (const supply of suppliesOf(servicoId, supplies)) {
          await this.db.create('stock_movement', {
            product_id: supply.product_id,
            kind: 'use',
            qty: -supply.qty,
            appointment_id: id,
            occurred_at: now,
          });
        }
      }

      const sale = input.products?.length
        ? await this.recordSale({ items: input.products, clientId: a.client_id, appointmentId: id, method: input.method, now })
        : null;

      let packageUsed: CompleteResult['packageUsed'] = null;
      if (pkg) {
        const updated = await this.db.all('appointment');
        packageUsed = { pkg: pkg.pkg, remaining: packageBalance(pkg.pkg, updated, today).remaining };
      }
      return { appointment, payment, packageUsed, sale };
    });
  }

  /** Estorno explícito: o atendimento volta para agendado e os pagamentos dele são excluídos. */
  async reverseCompletion(id: string): Promise<Appointment> {
    const a = await this.requireAppointment(id);
    if (a.status !== 'completed') return a;
    return this.db.batch(async () => {
      // Volta tudo o que a conclusão lançou: pagamentos, a venda de produtos junto e o gasto do serviço.
      const [payments, sales, movements] = await Promise.all([this.db.all('payment'), this.db.all('sale'), this.db.all('stock_movement')]);
      const saleIds = new Set(sales.filter((x) => x.appointment_id === id && !x.deleted_at).map((x) => x.id));
      for (const p of payments) if (p.appointment_id === id || (p.sale_id && saleIds.has(p.sale_id))) await this.db.remove('payment', p.id);
      for (const m of movements) if (!m.deleted_at && (m.appointment_id === id || (m.sale_id && saleIds.has(m.sale_id)))) await this.db.remove('stock_movement', m.id);
      for (const saleId of saleIds) await this.db.remove('sale', saleId);
      return this.db.update('appointment', id, {
        status: 'scheduled',
        completed_at: null,
        package_id: null,
        _reverse: true,
      } as Partial<Appointment>);
    });
  }

  /** Receber depois (o que falta receber). */
  async registerPayment(input: { appointmentId: string; amountCents: number; method: PaymentMethod }): Promise<Payment> {
    const a = await this.requireAppointment(input.appointmentId);
    if (input.amountCents <= 0) throw new DomainError('invalid_input', 'Informe o valor recebido.');
    return this.db.create('payment', {
      appointment_id: a.id,
      client_id: a.client_id,
      amount_cents: input.amountCents,
      method: input.method,
      paid_at: this.db.nowIso(),
    });
  }

  /** "Manter os dois" no aviso de choque de horário. */
  async keepOverlap(ids: string[]): Promise<void> {
    await this.db.batch(async () => {
      for (const id of ids) await this.db.update('appointment', id, { overlap_ok: true });
    });
  }

  async updateAppointmentNotes(id: string, notes: string | null): Promise<Appointment> {
    return this.db.update('appointment', id, { notes: notes?.trim() || null });
  }

  // ================================================================ bloqueios

  async blockTime(start: string, end: string, reason: string | null = null) {
    if (toMs(end) <= toMs(start)) throw new DomainError('invalid_input', 'O fim precisa ser depois do início.');
    return this.db.create('block', { start_utc: toIso(start), end_utc: toIso(end), reason: reason?.trim() || null });
  }

  async unblock(id: string): Promise<void> {
    await this.db.remove('block', id);
  }

  // ================================================================ horários abertos

  /** Modo A: horário fixo da semana. Substitui todas as regras. */
  async setWeeklyRules(rules: Array<{ weekday: number; start: string; end: string }>): Promise<void> {
    await this.db.batch(async () => {
      const existing = await this.db.all('availability_rule');
      for (const r of existing) await this.db.remove('availability_rule', r.id);
      for (const r of rules) await this.db.create('availability_rule', r);
    });
  }

  /** Modo B: janelas abertas de uma semana (segunda a domingo). Substitui as da semana. */
  async setWeekWindows(
    weekStart: LocalDate,
    windows: Array<Pick<AvailabilityWindow, 'date' | 'start' | 'end' | 'kind' | 'region'>>,
  ): Promise<void> {
    const weekEnd = addDays(weekStart, 6);
    await this.db.batch(async () => {
      const existing = await this.db.range('availability_window', 'date', weekStart, weekEnd);
      for (const w of existing) await this.db.remove('availability_window', w.id);
      for (const w of windows) {
        if (w.date < weekStart || w.date > weekEnd) continue;
        await this.db.create('availability_window', w);
      }
    });
  }

  /** "Repetir a semana passada" (épico 3.3). Devolve quantas faixas foram copiadas. */
  async repeatPreviousWeek(weekStart: LocalDate): Promise<number> {
    const prevStart = addDays(weekStart, -7);
    const previous = await this.db.range('availability_window', 'date', prevStart, addDays(prevStart, 6));
    const shifted = previous.map((w) => ({
      date: addDays(w.date, 7),
      start: w.start,
      end: w.end,
      kind: w.kind,
      region: w.region,
    }));
    await this.setWeekWindows(weekStart, shifted);
    return shifted.length;
  }

  async addHomeWindow(date: LocalDate, start: string, end: string, region: string | null) {
    return this.db.create('availability_window', { date, start, end, kind: 'home_window', region: region?.trim() || null });
  }

  async removeWindow(id: string): Promise<void> {
    await this.db.remove('availability_window', id);
  }

  // ================================================================ pacotes

  async sellPackage(input: {
    clientId: string;
    serviceId: string;
    sessionsTotal: number;
    sessionsUsedInitial?: number;
    priceCents: number;
    method?: PaymentMethod | null;
    expiresAt?: LocalDate | null;
  }): Promise<{ pkg: Package; payment: Payment | null }> {
    await this.requireCanCreate();
    if (input.sessionsTotal < 1) throw new DomainError('invalid_input', 'Informe quantas sessões tem o pacote.');
    return this.db.batch(async () => {
      const now = this.db.nowIso();
      const pkg = await this.db.create('package', {
        client_id: input.clientId,
        service_id: input.serviceId,
        sessions_total: input.sessionsTotal,
        sessions_used_initial: input.sessionsUsedInitial ?? 0,
        price_cents: input.priceCents,
        purchased_at: now,
        expires_at: input.expiresAt ?? null,
      });
      let payment: Payment | null = null;
      if (input.method && input.priceCents > 0) {
        payment = await this.db.create('payment', {
          package_id: pkg.id,
          client_id: input.clientId,
          amount_cents: input.priceCents,
          method: input.method,
          paid_at: now,
        });
      }
      return { pkg, payment };
    });
  }

  async deletePackage(id: string): Promise<void> {
    await this.db.remove('package', id);
  }

  // ================================================================ estoque

  /** Produto novo; com quantidade, já lança a primeira entrada (o que ela tem hoje). */
  async createProduct(input: ProductInput & { initialPacks?: number }): Promise<Product> {
    const name = input.name.trim();
    if (!name) throw new DomainError('invalid_input', 'Escreva o nome do produto.');
    const unit = input.unit ?? 'un';
    const packSize = unit === 'un' ? 1 : Math.max(1, Math.round(input.pack_size ?? 1));
    return this.db.batch(async () => {
      const product = await this.db.create('product', {
        name,
        unit,
        pack_size: packSize,
        cost_cents: Math.max(0, input.cost_cents ?? 0),
        price_cents: input.price_cents ?? null,
        min_qty: input.min_qty ?? null,
        active: true,
      });
      const packs = input.initialPacks ?? 0;
      if (packs > 0) await this.addStock({ productId: product.id, packs, costPerPackCents: product.cost_cents });
      return product;
    });
  }

  async updateProduct(id: string, patch: Partial<ProductInput>): Promise<Product> {
    const next: Partial<Product> = { ...patch };
    if (patch.name !== undefined) {
      next.name = patch.name.trim();
      if (!next.name) throw new DomainError('invalid_input', 'Escreva o nome do produto.');
    }
    if (patch.unit === 'un') next.pack_size = 1;
    return this.db.update('product', id, next);
  }

  /** Produto sai da lista, mas as vendas e entradas antigas continuam com o nome. */
  async archiveProduct(id: string): Promise<Product> {
    return this.db.batch(async () => {
      for (const s of await this.db.all('service_supply')) if (s.product_id === id && !s.deleted_at) await this.db.remove('service_supply', s.id);
      return this.db.update('product', id, { active: false });
    });
  }

  /** Desfazer "Tirar do estoque" (o gasto nos serviços não volta). */
  async restoreProduct(id: string): Promise<Product> {
    return this.db.update('product', id, { active: true });
  }

  /** Entrada no estoque (compra): embalagens e quanto pagou em cada. */
  async addStock(input: { productId: string; packs: number; costPerPackCents?: number; note?: string | null }): Promise<StockMovement> {
    const product = await this.requireProduct(input.productId);
    if (!(input.packs > 0)) throw new DomainError('invalid_input', 'Informe quantas embalagens entraram.');
    const cost = Math.max(0, Math.round(input.costPerPackCents ?? product.cost_cents));
    return this.db.batch(async () => {
      if (cost !== product.cost_cents && cost > 0) await this.db.update('product', product.id, { cost_cents: cost });
      return this.db.create('stock_movement', {
        product_id: product.id,
        kind: 'in',
        qty: Math.round(input.packs * product.pack_size),
        cost_cents: Math.round(cost * input.packs),
        note: input.note ?? null,
        occurred_at: this.db.nowIso(),
      });
    });
  }

  /** Correção da contagem ("contei e tenho 7"): lança a diferença. */
  async setStockCount(productId: string, qty: number, note: string | null = null): Promise<StockMovement | null> {
    await this.requireProduct(productId);
    const current = stockByProduct(await this.db.all('stock_movement')).get(productId) ?? 0;
    const delta = Math.round(qty) - current;
    if (delta === 0) return null;
    return this.db.create('stock_movement', { product_id: productId, kind: 'adjust', qty: delta, note, occurred_at: this.db.nowIso() });
  }

  /** Desfazer uma entrada ou uma correção recém-lançada. */
  async removeStockMovement(id: string): Promise<void> {
    const m = await this.db.get('stock_movement', id);
    if (!m || m.deleted_at) return;
    if (m.kind !== 'in' && m.kind !== 'adjust') throw new DomainError('invalid_input', 'Esse movimento sai junto com a venda ou o atendimento.');
    await this.db.remove('stock_movement', id);
  }

  /** O que um serviço gasta a cada atendimento (substitui a lista do serviço). */
  async setServiceSupplies(serviceId: string, items: Array<{ productId: string; qty: number }>): Promise<ServiceSupply[]> {
    const wanted = new Map<string, number>();
    for (const i of items) if (i.qty > 0) wanted.set(i.productId, Math.round(i.qty));
    return this.db.batch(async () => {
      const current = suppliesOf(serviceId, await this.db.all('service_supply'));
      const out: ServiceSupply[] = [];
      for (const s of current) {
        const qty = wanted.get(s.product_id);
        if (qty === undefined) await this.db.remove('service_supply', s.id);
        else {
          out.push(qty === s.qty ? s : await this.db.update('service_supply', s.id, { qty }));
          wanted.delete(s.product_id);
        }
      }
      for (const [productId, qty] of wanted) out.push(await this.db.create('service_supply', { service_id: serviceId, product_id: productId, qty }));
      return out;
    });
  }

  /** Venda avulsa (no Caixa): quem passou só para comprar. Sem forma de pagamento fica "a receber". */
  async sellProducts(input: { items: SaleItemInput[]; clientId?: string | null; method: PaymentMethod | null }): Promise<Sale> {
    if (!input.items.some((i) => i.packs > 0)) throw new DomainError('invalid_input', 'Escolha pelo menos um produto.');
    return this.db.batch(async () => this.recordSale({ ...input, appointmentId: null, now: this.db.nowIso() }));
  }

  /** Receber depois uma venda de produto. */
  async registerSalePayment(input: { saleId: string; amountCents: number; method: PaymentMethod }): Promise<Payment> {
    const sale = await this.db.get('sale', input.saleId);
    if (!sale || sale.deleted_at) throw new DomainError('not_found', 'Venda não encontrada.');
    if (input.amountCents <= 0) throw new DomainError('invalid_input', 'Informe o valor recebido.');
    return this.db.create('payment', {
      sale_id: sale.id,
      client_id: sale.client_id,
      amount_cents: input.amountCents,
      method: input.method,
      paid_at: this.db.nowIso(),
    });
  }

  /** Desfazer uma venda avulsa: some a venda, os itens voltam para o estoque e o pagamento sai do caixa. */
  async deleteSale(id: string): Promise<void> {
    await this.db.batch(async () => {
      for (const p of await this.db.all('payment')) if (p.sale_id === id && !p.deleted_at) await this.db.remove('payment', p.id);
      for (const m of await this.db.all('stock_movement')) if (m.sale_id === id && !m.deleted_at) await this.db.remove('stock_movement', m.id);
      await this.db.remove('sale', id);
    });
  }

  private async recordSale(input: {
    items: SaleItemInput[];
    clientId?: string | null;
    appointmentId: string | null;
    method: PaymentMethod | null;
    now: string;
  }): Promise<Sale> {
    const products = new Map((await this.db.all('product')).map((p) => [p.id, p]));
    const items = input.items.filter((i) => i.packs > 0 && products.has(i.productId));
    const total = saleTotal(items, products);
    const sale = await this.db.create('sale', {
      client_id: input.clientId ?? null,
      appointment_id: input.appointmentId,
      total_cents: total,
      sold_at: input.now,
    });
    for (const i of items) {
      const p = products.get(i.productId)!;
      await this.db.create('stock_movement', {
        product_id: p.id,
        kind: 'sale',
        qty: -Math.round(i.packs * p.pack_size),
        price_cents: i.packs * (p.price_cents ?? 0),
        appointment_id: input.appointmentId,
        sale_id: sale.id,
        occurred_at: input.now,
      });
    }
    if (input.method && total > 0) {
      await this.db.create('payment', { sale_id: sale.id, client_id: input.clientId ?? null, amount_cents: total, method: input.method, paid_at: input.now });
    }
    return sale;
  }

  private async requireProduct(id: string): Promise<Product> {
    const p = await this.db.get('product', id);
    if (!p || p.deleted_at) throw new DomainError('not_found', 'Produto não encontrado.');
    return p;
  }

  // ================================================================ mensagens

  async saveTemplate(kind: MessageKind, text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) throw new DomainError('invalid_input', 'A mensagem não pode ficar vazia.');
    const existing = (await this.db.all('message_template')).filter((t) => t.kind === kind);
    await this.db.batch(async () => {
      const [first, ...rest] = existing;
      if (first) await this.db.update('message_template', first.id, { text: trimmed });
      else await this.db.create('message_template', { kind, text: trimmed });
      for (const r of rest) await this.db.remove('message_template', r.id);
    });
  }

  async resetTemplate(kind: MessageKind): Promise<void> {
    const existing = (await this.db.all('message_template')).filter((t) => t.kind === kind);
    await this.db.batch(async () => {
      for (const t of existing) await this.db.remove('message_template', t.id);
    });
  }

  // ================================================================ auxiliares

  private async requireAppointment(id: string): Promise<Appointment> {
    const a = await this.db.get('appointment', id);
    if (!a) throw new DomainError('not_found', 'Atendimento não encontrado.');
    return a;
  }

  /** Atendimentos de um dia antes a um dia depois do intervalo (para checar choque). */
  private async appointmentsAround(start: string, end: string): Promise<Appointment[]> {
    const lower = toIso(toMs(start) - 24 * 3_600_000);
    const upper = toIso(toMs(end) + 24 * 3_600_000);
    return this.db.range('appointment', 'start_utc', lower, upper);
  }
}
